import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';

import { DuelChipStackPng, DuelIconClockSvg } from '../../../assets/duels';
import { localizeTableName } from '../../../utils/helpers/localizeTableName';
import {
	MY_TABLES_GOLD_FILL,
	myTablesColors,
	tableImage,
	useMyTablesBarSummary,
} from '../MyTables/myTablesShared';
import { GradientFill } from './GradientFill';
import { Pressable } from './Touchable';

// "My Active & Recent Tables" (the ticket's mock, three states): a dark maroon plate on a thin gold
// rim with the overline, a row of round seats - the active tables by name, else the recent ones by
// city art - with a status line under them, and the gold View Tables button. Shared by the home
// redesign, the lobby carousel screen and the VIP rooms screen. Every number below is in a
// 361-wide design space.
const DESIGN_WIDTH = 361;
export const RECENT_TABLES_BAR_DESIGN_HEIGHT = 84;
const SEAT = 34;
const BUTTON = { width: 104, height: 36 };

/** The bar's height on screen for a given width. */
export const getRecentTablesBarHeight = (width: number) =>
	(width / DESIGN_WIDTH) * RECENT_TABLES_BAR_DESIGN_HEIGHT;

type Props = {
	/** Width the 361-wide design bar should occupy. */
	width: number;
	onPress: () => void;
};

export const RecentTablesBar = ({ width, onPress }: Props) => {
	const { t } = useTranslation();
	const summary = useMyTablesBarSummary();
	const scale = width / DESIGN_WIDTH;
	const styles = createStyles(scale);

	return (
		<Pressable onPress={onPress} style={styles.bar}>
			<View style={styles.content}>
				<Text numberOfLines={1} style={styles.title}>
					{t('T_SITE_MY_TABLES_BAR_TITLE', { defaultValue: 'My active & recent tables' })}
				</Text>

				{summary.kind === 'empty' ? (
					<View style={styles.emptyRow}>
						<Image source={DuelChipStackPng} resizeMode='contain' style={styles.emptyImage} />
						<View style={styles.emptyText}>
							<Text numberOfLines={1} style={styles.emptyTitle}>
								{summary.message}
							</Text>
							<Text numberOfLines={1} style={styles.emptyDetail}>
								{summary.detail}
							</Text>
						</View>
					</View>
				) : (
					<>
						<View style={styles.seatRow}>
							{summary.seats.map(seat =>
								summary.kind === 'active' ? (
									<View key={seat.key} style={[styles.seat, styles.nameSeat]}>
										<Text numberOfLines={2} style={styles.seatName}>
											{localizeTableName(seat.rawName).replace(/\s+(\S+)$/, '\n$1')}
										</Text>
									</View>
								) : (
									<View key={seat.key} style={styles.seat}>
										<Image
											source={tableImage(seat.rawName)}
											resizeMode='cover'
											style={styles.seatImage}
										/>
									</View>
								),
							)}
							{(summary.kind === 'active' || summary.overflow > 0) && (
								<View
									style={[
										styles.seat,
										styles.overflowSeat,
										summary.kind === 'active' && styles.overflowSeatDashed,
									]}
								>
									<Text
										style={[
											styles.overflowText,
											summary.kind === 'active' && styles.overflowTextDim,
										]}
									>
										+{summary.overflow}
									</Text>
								</View>
							)}
						</View>
						<View style={styles.statusRow}>
							{summary.kind === 'active' ? (
								<View style={styles.activeDot} />
							) : (
								<DuelIconClockSvg width={11 * scale} height={11 * scale} />
							)}
							<Text numberOfLines={1} style={styles.status}>
								{summary.message}
							</Text>
						</View>
					</>
				)}
			</View>

			<View style={styles.viewButton}>
				<GradientFill stops={MY_TABLES_GOLD_FILL} radius={8 * scale} />
				<Text numberOfLines={1} adjustsFontSizeToFit style={styles.viewText}>
					{t('T_SITE_MY_TABLES_VIEW', { defaultValue: 'View tables' })}
				</Text>
			</View>
		</Pressable>
	);
};

const createStyles = (scale: number) => {
	const s = (value: number) => value * scale;
	const hairline = Math.max(1, s(1));

	return StyleSheet.create({
		bar: {
			width: s(DESIGN_WIDTH),
			height: s(RECENT_TABLES_BAR_DESIGN_HEIGHT),
			alignSelf: 'center',
			borderRadius: s(14),
			borderWidth: hairline,
			borderColor: myTablesColors.rim,
			backgroundColor: myTablesColors.panel,
			flexDirection: 'row',
			alignItems: 'center',
			paddingLeft: s(12),
			paddingRight: s(12),
			columnGap: s(10),
		},
		content: {
			flex: 1,
			alignSelf: 'stretch',
			justifyContent: 'center',
			rowGap: s(5),
		},
		title: {
			fontFamily: 'Sofia Sans',
			fontSize: s(11.5),
			lineHeight: s(14),
			fontWeight: '800',
			letterSpacing: s(0.4),
			textTransform: 'uppercase',
			color: myTablesColors.gold,
		},
		seatRow: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: s(5),
		},
		seat: {
			width: s(SEAT),
			height: s(SEAT),
			borderRadius: s(SEAT / 2),
			borderWidth: Math.max(1, s(1.5)),
			borderColor: myTablesColors.rim,
			backgroundColor: myTablesColors.well,
			overflow: 'hidden',
			alignItems: 'center',
			justifyContent: 'center',
		},
		nameSeat: {
			backgroundColor: myTablesColors.seat,
		},
		seatName: {
			fontFamily: 'Sofia Sans',
			fontSize: s(9),
			lineHeight: s(10),
			fontWeight: '700',
			textAlign: 'center',
			color: '#FFFFFF',
		},
		// The city art is a portrait plinth with the landmark in its upper half; scaled past the
		// circle and nudged down so the circle frames the landmark rather than the base.
		seatImage: {
			position: 'absolute',
			width: s(SEAT * 1.3),
			height: s(SEAT * 1.3 * (4 / 3)),
			left: s(-SEAT * 0.15),
			top: s(-SEAT * 0.2),
		},
		overflowSeat: {
			backgroundColor: 'transparent',
		},
		overflowSeatDashed: {
			borderStyle: 'dashed',
			borderColor: myTablesColors.rimDim,
		},
		overflowText: {
			fontFamily: 'Sofia Sans',
			fontSize: s(12),
			fontWeight: '700',
			color: myTablesColors.gold,
		},
		overflowTextDim: {
			color: myTablesColors.muted,
		},
		statusRow: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: s(5),
		},
		activeDot: {
			width: s(8),
			height: s(8),
			borderRadius: s(4),
			backgroundColor: myTablesColors.green,
		},
		status: {
			flexShrink: 1,
			fontFamily: 'Sofia Sans',
			fontSize: s(11),
			lineHeight: s(13),
			fontWeight: '600',
			color: '#FFFFFF',
		},
		emptyRow: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: s(8),
		},
		emptyImage: {
			width: s(42),
			height: s(42),
		},
		emptyText: {
			flex: 1,
			rowGap: s(2),
		},
		emptyTitle: {
			fontFamily: 'Sofia Sans',
			fontSize: s(12),
			lineHeight: s(15),
			fontWeight: '600',
			color: '#FFFFFF',
		},
		emptyDetail: {
			fontFamily: 'Sofia Sans',
			fontSize: s(10.5),
			lineHeight: s(13),
			color: myTablesColors.secondary,
		},
		viewButton: {
			width: s(BUTTON.width),
			height: s(BUTTON.height),
			borderRadius: s(8),
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: s(6),
			shadowColor: '#FFB800',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.35,
			shadowRadius: s(6),
		},
		viewText: {
			fontFamily: 'Sofia Sans',
			fontSize: s(12),
			fontWeight: '800',
			letterSpacing: s(0.3),
			textTransform: 'uppercase',
			color: myTablesColors.onGold,
		},
	});
};

export default RecentTablesBar;
